import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Check, Clock } from 'lucide-react';
import { useTheme } from '../ThemeContext';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

// ==========================================
// CUSTOM CALENDAR
// ==========================================
function Calendar({ selectedDate, onSelect, t, isDark }) {
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(selectedDate ? selectedDate.getMonth() : today.getMonth());
  const [viewYear, setViewYear] = useState(selectedDate ? selectedDate.getFullYear() : today.getFullYear());

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();

  const prev = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const next = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;
    return selectedDate.getDate() === day && selectedDate.getMonth() === viewMonth && selectedDate.getFullYear() === viewYear;
  };
  const isToday = (day) => today.getDate() === day && today.getMonth() === viewMonth && today.getFullYear() === viewYear;
  const isPast = (day) => new Date(viewYear, viewMonth, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const navBtn = { background: 'none', border: 'none', color: t.textSecondary, cursor: 'pointer', padding: '4px', borderRadius: '4px', display: 'flex' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <button onClick={prev} style={navBtn}
          onMouseEnter={e => e.currentTarget.style.color = t.text}
          onMouseLeave={e => e.currentTarget.style.color = t.textSecondary}
        ><ChevronLeft size={16} /></button>
        <span style={{ fontSize: '13px', fontWeight: 600, color: t.text, fontFamily: 'Poppins, sans-serif' }}>
          {MONTHS[viewMonth]} {viewYear}
        </span>
        <button onClick={next} style={navBtn}
          onMouseEnter={e => e.currentTarget.style.color = t.text}
          onMouseLeave={e => e.currentTarget.style.color = t.textSecondary}
        ><ChevronRight size={16} /></button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '4px' }}>
        {DAYS.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '10px', fontWeight: 600, color: t.textTertiary, fontFamily: 'Poppins, sans-serif', padding: '4px 0', letterSpacing: '0.05em' }}>{d}</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const past = isPast(day);
          const sel = isSelected(day);
          const tod = isToday(day);
          return (
            <button
              key={day}
              disabled={past}
              onClick={() => onSelect(new Date(viewYear, viewMonth, day))}
              style={{
                width: '32px', height: '32px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto', fontSize: '12px', fontFamily: 'Poppins, sans-serif',
                fontWeight: sel ? 600 : 400,
                backgroundColor: sel ? t.buttonBg : 'transparent',
                color: past ? t.textTertiary : sel ? t.buttonText : tod ? t.text : t.textSecondary,
                border: tod && !sel ? `1px solid ${t.borderStrong}` : 'none',
                borderRadius: '6px',
                cursor: past ? 'not-allowed' : 'pointer',
                transition: 'all 0.1s',
              }}
              onMouseEnter={e => { if (!past && !sel) e.currentTarget.style.backgroundColor = isDark ? 'rgba(245,230,200,0.06)' : 'rgba(61,36,18,0.04)'; }}
              onMouseLeave={e => { if (!past && !sel) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ==========================================
// CUSTOM TIME PICKER
// ==========================================
function TimePicker({ hour, minute, period, setHour, setMinute, setPeriod, t, isDark }) {
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const colStyle = {
    maxHeight: '140px', overflowY: 'auto', borderRadius: '6px',
    border: `1px solid ${t.borderStrong}`, backgroundColor: isDark ? 'rgba(245,230,200,0.02)' : 'rgba(61,36,18,0.02)',
  };
  const itemStyle = (active) => ({
    display: 'block', width: '100%', padding: '6px 8px', textAlign: 'center',
    background: active ? (isDark ? 'rgba(212,165,116,0.2)' : 'rgba(61,36,18,0.08)') : 'transparent',
    border: 'none', color: active ? t.text : t.textSecondary,
    fontSize: '13px', fontFamily: 'Poppins, sans-serif', cursor: 'pointer',
    fontWeight: active ? 600 : 400,
  });
  const labelStyle = { fontSize: '10px', fontWeight: 600, color: t.textTertiary, marginBottom: '6px', fontFamily: 'Poppins, sans-serif', letterSpacing: '0.05em' };

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
      <div style={{ flex: 1 }}>
        <div style={labelStyle}>HOUR</div>
        <div style={colStyle}>
          {hours.map(h => (
            <button key={h} onClick={() => setHour(h)} style={itemStyle(hour === h)}
              onMouseEnter={e => { if (hour !== h) e.currentTarget.style.backgroundColor = isDark ? 'rgba(245,230,200,0.04)' : 'rgba(61,36,18,0.03)'; }}
              onMouseLeave={e => { if (hour !== h) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >{h}</button>
          ))}
        </div>
      </div>
      <div style={{ flex: 1 }}>
        <div style={labelStyle}>MIN</div>
        <div style={colStyle}>
          {minutes.map(m => (
            <button key={m} onClick={() => setMinute(m)} style={itemStyle(minute === m)}
              onMouseEnter={e => { if (minute !== m) e.currentTarget.style.backgroundColor = isDark ? 'rgba(245,230,200,0.04)' : 'rgba(61,36,18,0.03)'; }}
              onMouseLeave={e => { if (minute !== m) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >{String(m).padStart(2, '0')}</button>
          ))}
        </div>
      </div>
      <div>
        <div style={labelStyle}>&nbsp;</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {['AM', 'PM'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={{
              padding: '8px 12px',
              backgroundColor: period === p ? t.buttonBg : 'transparent',
              border: period === p ? 'none' : `1px solid ${t.borderStrong}`,
              borderRadius: '6px',
              color: period === p ? t.buttonText : t.textSecondary,
              fontSize: '12px', fontWeight: 600, fontFamily: 'Poppins, sans-serif',
              cursor: 'pointer', transition: 'all 0.1s',
            }}>{p}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// SCHEDULE POPOVER (exported)
// ==========================================
export default function SchedulePicker({ scheduleOn, scheduledAt, setScheduleOn, setScheduledAt, onClose }) {
  const { t, isDark } = useTheme();
  const pickerRef = useRef(null);
  const [mode, setMode] = useState(scheduleOn ? 'scheduled' : 'now');
  const [selectedDate, setSelectedDate] = useState(scheduledAt ? new Date(scheduledAt) : null);
  const [hour, setHour] = useState(scheduledAt ? (new Date(scheduledAt).getHours() % 12 || 12) : 12);
  const [minute, setMinute] = useState(scheduledAt ? new Date(scheduledAt).getMinutes() : 0);
  const [period, setPeriod] = useState(scheduledAt ? (new Date(scheduledAt).getHours() >= 12 ? 'PM' : 'AM') : 'PM');

  // Click-outside handler using document listener (not backdrop)
  // This works even though the picker is inside a sticky toolbar stacking context
  useEffect(() => {
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  useEffect(() => {
    if (mode === 'scheduled' && selectedDate) {
      const h24 = period === 'PM' ? (hour === 12 ? 12 : hour + 12) : (hour === 12 ? 0 : hour);
      const d = new Date(selectedDate);
      d.setHours(h24, minute, 0, 0);
      setScheduleOn(true);
      setScheduledAt(d.getTime());
    } else if (mode === 'now') {
      setScheduleOn(false);
      setScheduledAt(null);
    }
  }, [mode, selectedDate, hour, minute, period]);

  const formatSummary = () => {
    if (!selectedDate) return null;
    return `${selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at ${hour}:${String(minute).padStart(2,'0')} ${period}`;
  };

  return (
    <div ref={pickerRef} style={{
      position: 'absolute', top: 'calc(100% + 4px)', right: 0,
      width: '320px',
      backgroundColor: t.modalBg, border: `1px solid ${t.borderStrong}`,
      borderRadius: '8px', zIndex: 50,
      boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.6)' : '0 16px 48px rgba(0,0,0,0.15)',
      padding: '14px',
    }}>
      <div style={{ fontSize: '11px', fontWeight: 600, color: t.textTertiary, marginBottom: '10px', fontFamily: 'Poppins, sans-serif', letterSpacing: '0.05em' }}>
        SCHEDULE
      </div>

      {/* Mode toggles — side by side */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
        {[{ key: 'now', label: 'Now' }, { key: 'scheduled', label: 'Schedule' }].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => { setMode(key); if (key === 'now') { setScheduleOn(false); setScheduledAt(null); } }}
            style={{
              flex: 1, padding: '8px',
              backgroundColor: mode === key ? (isDark ? 'rgba(212,165,116,0.12)' : 'rgba(61,36,18,0.06)') : 'transparent',
              border: `1px solid ${mode === key ? t.accent : t.borderStrong}`,
              borderRadius: '6px',
              color: mode === key ? t.text : t.textSecondary,
              fontSize: '13px', fontFamily: 'Poppins, sans-serif', cursor: 'pointer',
              fontWeight: mode === key ? 600 : 400,
              transition: 'all 0.15s',
            }}
          >{label}</button>
        ))}
      </div>

      {mode === 'scheduled' && (
        <div>
          <Calendar selectedDate={selectedDate} onSelect={setSelectedDate} t={t} isDark={isDark} />
          <div style={{ height: '1px', background: t.borderStrong, margin: '12px 0' }} />
          <TimePicker hour={hour} minute={minute} period={period} setHour={setHour} setMinute={setMinute} setPeriod={setPeriod} t={t} isDark={isDark} />

          {formatSummary() && (
            <div style={{
              marginTop: '12px', padding: '8px 10px',
              backgroundColor: isDark ? 'rgba(245,230,200,0.04)' : 'rgba(61,36,18,0.03)',
              borderRadius: '6px',
              fontSize: '12px', fontFamily: 'Poppins, sans-serif',
              color: t.textSecondary, textAlign: 'center',
            }}>
              {formatSummary()}
            </div>
          )}
        </div>
      )}

      {/* Apply button */}
      <button
        onClick={onClose}
        style={{
          width: '100%', marginTop: '12px', padding: '9px',
          backgroundColor: (mode === 'scheduled' && selectedDate) ? t.buttonBg : 'transparent',
          border: (mode === 'scheduled' && selectedDate) ? 'none' : `1px solid ${t.borderStrong}`,
          borderRadius: '6px',
          color: (mode === 'scheduled' && selectedDate) ? t.buttonText : t.textSecondary,
          fontSize: '13px', fontWeight: 600, fontFamily: 'Poppins, sans-serif',
          cursor: 'pointer', transition: 'all 0.15s',
        }}
      >
        Apply
      </button>
    </div>
  );
}